import AsyncGroupFactory from '@/Factories/AsyncGroupFactory';
import ComponentManager from '@/Management/ComponentManager';
import ExpanderGroupBoxViewModelT from '@/ViewModels/Controls/ExpanderGroupBoxViewModel';
import ExpanderGroupBoxT from '@/Controls/ExpanderGroupBox';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import LinkViewModelT from '@/ViewModels/Controls/LinkViewModel';
import StorageHelper from '@/Utility/StorageHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class ExpanderGroupFactory extends AsyncGroupFactory {

    private ExpanderGroupBoxViewModel = ComponentManager.getViewModel<typeof ExpanderGroupBoxViewModelT>('ExpanderGroupBoxViewModel');
    private ExpanderGroupBox = ComponentManager.getControl<typeof ExpanderGroupBoxT>('ExpanderGroupBox');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');

    /**
     * Definiert ob der Expander immer initial geschlossen sein soll
     */
    public alwaysClosed: boolean;

    /**
     * Definiert ob der Expander aktuell geschlossen ist
     */
    public isCollapsed: KnockoutObservable<boolean>;

    /**
    * Definiert die Sichtbarkeit des Expanders
    */
    public isDisplayed: KnockoutObservable<boolean>;

    /**
     * @override
     */
    public viewModel: ExpanderGroupBoxViewModelT;

    /**
     * Gibt das Level (in der Hierarchie) der Gruppe an
     */
    public level: number;

    public constructor(params: any) {
        super(params);
        this.layout = this.paramOrDefault(params.layout, 'OneColumnLayout');
        this.colspan = this.paramOrDefault(params.colspan, 'Fill');
        this.alwaysClosed = this.paramOrDefault(params.alwaysClosed, false);
        this.isDisplayed = this.observableOrDefault(params.isDisplayed, true);
        this.level = this.paramOrDefault(params.level, 1);

        if (!this.alwaysClosed) {
            this.isCollapsed = StorageHelper.bind(this.getFullGroupErgName() + '_Collapsed', params.isCollapsed || false);
        }
        else {
            this.isCollapsed = this.observableOrDefault(true);
        }
    }

    /**
     * @override 
     */
    protected createViewModel(): ExpanderGroupBoxViewModelT {
        return new this.ExpanderGroupBoxViewModel({
            context: this.context,
            isDisplayed: this.isDisplayed,
            isVisible: this.isVisible,
            isCollapsed: this.isCollapsed,
            isCollapsedWithAncestors: this.defineComputed(() => {
                return this.isCollapsed() || this.ancestorIsCollapsed();
            }),
            level: this.level,
            layout: this.layout,
            parentLocator: this.locator,
            locator: this.detailViewGroup.name,
            isSummaryViewMode: this.isSummaryViewMode,
            detailViewGroups: this.detailViewGroup.detailViewGroups,
            detailViewItems: this.detailViewGroup.items,
            link: new this.LinkViewModel({
                parentLocator: this.locator,
                locator: this.detailViewGroup.name,
                image: new this.ImageViewModel({
                    source: this.defineComputed(() => {
                        if (this.isCollapsed()) {
                            return GlobalConfiguration.inlineImages.EXPANDER_EXPAND;
                        } else {
                            return GlobalConfiguration.inlineImages.EXPANDER_COLLAPSE;
                        }
                    }),
                    alternativeText: new this.LabelViewModel({
                        ergName: this.defineComputed(() => {
                            if (this.isCollapsed()) {
                                return 'FACTORY_EXPANDER_EXPAND';
                            } else {
                                return 'FACTORY_EXPANDER_COLLAPSE';
                            }
                        })
                    }),
                    parentLocator: this.locator,
                    locator: this.detailViewGroup.name + '.image'
                }),
                label: new this.LabelViewModel({
                    ergName: this.detailViewGroup.ergName,
                    parentLocator: this.locator,
                    locator: this.detailViewGroup.name + '.label'
                }),
                onClick: () => {
                    this.isCollapsed(!this.isCollapsed());
                },
                tooltip: new this.LabelViewModel({
                    ergName: this.defineComputed(() => {
                        if (this.isCollapsed()) {
                            return 'FACTORY_EXPANDER_EXPAND';
                        } else {
                            return 'FACTORY_EXPANDER_COLLAPSE';
                        }
                    })
                }),
                isFocusable: true
            })
        });
    }

    /**
     * @override 
     */
    protected createControl(): ExpanderGroupBoxT {
        return new this.ExpanderGroupBox(this.viewModel);
    }
}

ComponentManager.setFactory('ExpanderGroup', ExpanderGroupFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');